import { ChevronLeft, ChevronRight, Eye, X } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "./button";

interface Drawing {
    image_data: string;
    ai_interpretation: string;
    drawing_prompt: string;
    chapter: number;
}

interface DrawingCarouselModalProps {
    isOpen: boolean;
    onClose: () => void;
    drawings: Drawing[];
    initialIndex: number;
}

export function DrawingCarouselModal({ isOpen, onClose, drawings, initialIndex = 0 }: DrawingCarouselModalProps) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const posthog = usePostHog();

    const handlePrevious = useCallback(() => {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : drawings.length - 1;

        // Track carousel navigation
        posthog.capture("story_carousel_navigation", {
            direction: "previous",
            from_index: currentIndex,
            to_index: newIndex,
            from_chapter: drawings[currentIndex]?.chapter,
            to_chapter: drawings[newIndex]?.chapter,
            total_drawings: drawings.length,
            timestamp: new Date().toISOString(),
        });

        setCurrentIndex(newIndex);
    }, [currentIndex, drawings, posthog]);

    const handleNext = useCallback(() => {
        const newIndex = currentIndex < drawings.length - 1 ? currentIndex + 1 : 0;

        // Track carousel navigation
        posthog.capture("story_carousel_navigation", {
            direction: "next",
            from_index: currentIndex,
            to_index: newIndex,
            from_chapter: drawings[currentIndex]?.chapter,
            to_chapter: drawings[newIndex]?.chapter,
            total_drawings: drawings.length,
            timestamp: new Date().toISOString(),
        });

        setCurrentIndex(newIndex);
    }, [currentIndex, drawings, posthog]);

    const handleClose = useCallback(() => {
        // Track carousel closed for engagement analytics
        posthog.capture("story_carousel_closed", {
            final_index: currentIndex,
            final_chapter: drawings[currentIndex]?.chapter,
            total_drawings: drawings.length,
            timestamp: new Date().toISOString(),
        });

        onClose();
    }, [currentIndex, drawings, posthog, onClose]);

    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
            document.body.style.overflow = "hidden";

            // Track carousel opened for engagement analytics
            posthog.capture("story_carousel_opened", {
                initial_index: initialIndex,
                total_drawings: drawings.length,
                chapter: drawings[initialIndex]?.chapter,
                timestamp: new Date().toISOString(),
            });
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen, initialIndex, posthog, drawings]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            if (e.key === "Escape") {
                handleClose();
            } else if (e.key === "ArrowLeft") {
                handlePrevious();
            } else if (e.key === "ArrowRight") {
                handleNext();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, handleClose, handlePrevious, handleNext]);

    // Minimum swipe distance (in px)
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && drawings.length > 1) {
            handleNext();
        }
        if (isRightSwipe && drawings.length > 1) {
            handlePrevious();
        }
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    if (!isOpen || drawings.length === 0) return null;

    const currentDrawing = drawings[currentIndex];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center sm:items-start sm:pt-8 bg-black/80 backdrop-blur-sm overflow-y-auto p-4"
            onClick={handleBackdropClick}
        >
            <div
                className="relative w-full max-w-4xl mx-auto bg-background rounded-lg shadow-2xl overflow-hidden my-4 sm:my-8"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                {/* Close button */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClose}
                    className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 h-8 w-8 p-0 rounded-full bg-background/95 hover:bg-background text-primary shadow-lg"
                >
                    <X className="h-4 w-4" />
                </Button>

                {/* Navigation buttons - only show if more than 1 drawing */}
                {drawings.length > 1 && (
                    <>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handlePrevious}
                            className="absolute -left-4 md:left-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 p-0 rounded-full bg-background/95 hover:bg-background text-primary shadow-lg"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </Button>

                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleNext}
                            className="absolute -right-4 md:right-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 p-0 rounded-full bg-background/95 hover:bg-background text-primary shadow-lg"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </Button>
                    </>
                )}

                <div className="p-4 sm:p-6 space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
                    {/* Chapter indicator */}
                    <div className="text-center">
                        <span className="text-xs sm:text-sm text-primary font-semibold">
                            Chapter {currentDrawing.chapter}
                            {drawings.length > 1 && (
                                <span className="ml-2">
                                    ({currentIndex + 1} of {drawings.length})
                                </span>
                            )}
                        </span>
                    </div>

                    {/* Drawing prompt */}
                    <div className="text-center px-2">
                        <p className="text-xs sm:text-sm text-secondary italic leading-relaxed">
                            "{currentDrawing.drawing_prompt}"
                        </p>
                    </div>

                    {/* Main image */}
                    <div className="flex justify-center">
                        <div className="relative w-full max-w-2xl bg-background rounded-lg border border-border shadow-sm overflow-hidden">
                            <img
                                src={currentDrawing.image_data}
                                alt={`Drawing of ${currentDrawing.drawing_prompt}`}
                                className="w-full h-auto object-contain p-2 sm:p-4"
                            />
                        </div>
                    </div>

                    {/* AI interpretation */}
                    <div className="page-gradient p-3 sm:p-4 rounded-lg border border-border/50">
                        <div className="mb-2 flex items-center gap-2">
                            <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                            <h4 className="font-medium text-primary text-sm sm:text-base">What I See</h4>
                        </div>
                        <p className="text-foreground text-sm sm:text-base leading-relaxed">
                            <span className="font-semibold">{currentDrawing.ai_interpretation}</span>
                        </p>
                    </div>

                    {/* Dot indicators for multiple drawings */}
                    {drawings.length > 1 && (
                        <div className="flex justify-center gap-3 pt-3">
                            {drawings.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`ultra-compact w-4 h-4 rounded-full transition-all cursor-pointer ${
                                        index === currentIndex ? "bg-primary" : "bg-gray-400 hover:bg-gray-500"
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
